import { Link } from "react-router-dom";
import { Briefcase } from "lucide-react";
import { Button } from "components/ui/Button";
import { WhatsappNumberConnectionIcon } from "components/WhatsappNumberConnectionIcon";
import {
  WhatsAppTemplatesStatusNotice,
  type WappTemplatesStatus,
} from "components/WhatsAppTemplatesStatusNotice";
import { WhatsAppDisconnectedNotice } from "components/WhatsAppDisconnectedNotice";
import { ELORIA_WHATSAPP_NUMBER } from "constants/eloriaContact";

export function formatEloriaWhatsAppDisplay(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.startsWith("598") && d.length >= 11) {
    const rest = d.slice(3);
    return `+598 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
  }
  return d ? `+${d}` : digits;
}

/** Backend stores wapp_phone without leading + */
export function formatConnectedWappPhone(
  stored: string | null | undefined,
): string {
  if (!stored?.trim()) return "";
  const digits = stored.replace(/\D/g, "");
  if (!digits) return stored.trim();
  if (digits.startsWith("598") && digits.length >= 11) {
    const rest = digits.slice(3);
    return `+598 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
  }
  return `+${digits}`;
}

type Tr = (es: string, en: string) => string;

export function assistantNumberSectionTitle(
  tr: Tr,
  assistantName: string,
): string {
  const displayName = assistantName.trim() || "Eloria";
  return tr(`Número de ${displayName}`, `${displayName} number`);
}

export type WappAssistantMode = "exclusive" | "shared";

export type PropertyManagerWhatsappCardProps =
  | {
      variant: "settings";
      tr: Tr;
      assistantDisplayName: string;
      wappPhoneNumberId: string | null;
      wappPhone: string | null;
      wappAssistantMode: WappAssistantMode;
      onWappAssistantModeChange: (mode: WappAssistantMode) => void;
      wappAppId: string | null;
      wappConnectLoading: boolean;
      wappConnectMessage: string | null;
      /** Same Meta embedded-signup flow as "change number"; true when token is expiring or expired. */
      wappTokenReconnectRecommended?: boolean;
      wappTemplatesStatus?: WappTemplatesStatus;
      /** Meta revoked Eloria's access: the number is configured but no longer works. */
      wappDisconnected?: boolean;
      onLaunchSignup: () => void;
    }
  | {
      variant: "account";
      tr: Tr;
      assistantDisplayName?: string;
      wappPhoneNumberId: string | null;
      wappPhone: string | null;
    };

export function PropertyManagerWhatsappCard(
  props: PropertyManagerWhatsappCardProps,
) {
  const { tr, wappPhoneNumberId, wappPhone } = props;
  const disconnected =
    props.variant === "settings" && Boolean(props.wappDisconnected);

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 sm:p-4">
      <div className="mb-3 flex min-w-0 items-center gap-2.5">
        <Briefcase
          className="h-5 w-5 shrink-0 text-gray-600"
          strokeWidth={2}
          aria-hidden
        />
        <h2 className="text-base font-semibold tracking-tight text-gray-900">
          {assistantNumberSectionTitle(
            tr,
            props.variant === "settings"
              ? props.assistantDisplayName
              : props.assistantDisplayName ?? "Eloria",
          )}
        </h2>
      </div>
      {wappPhoneNumberId ? (
        <>
          <div className="space-y-1">
            <p className="text-xs leading-snug text-gray-600">
              {disconnected
                ? tr(
                    "Tu número de WhatsApp está desconectado.",
                    "Your WhatsApp number is disconnected.",
                  )
                : props.variant === "settings"
                  ? tr(
                      "Tu número de WhatsApp está conectado.",
                      "Your WhatsApp number is connected.",
                    )
                  : tr("Tu número conectado:", "Your connected number:")}
            </p>
            <div className="flex items-center gap-2.5">
              <WhatsappNumberConnectionIcon connected={!disconnected} />
              <p className="min-w-0 text-base font-semibold tabular-nums tracking-tight text-gray-900">
                {formatConnectedWappPhone(wappPhone) ||
                  tr("Sincronizando…", "Syncing…")}
              </p>
            </div>
          </div>
          {disconnected ? (
            <WhatsAppDisconnectedNotice tr={tr} />
          ) : props.variant === "settings" ? (
            <WhatsAppTemplatesStatusNotice
              tr={tr}
              status={props.wappTemplatesStatus}
              showWhenApproved
            />
          ) : null}
          {props.variant === "account" ? (
            <p className="mt-3 text-xs leading-snug text-gray-500">
              {tr(
                "Los residentes pueden escribirte a este número.",
                "Residents can message you on this number.",
              )}
            </p>
          ) : null}
          {props.variant === "settings" ? (
            <div className="mt-3 space-y-3">
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">
                  {tr("Modo del número", "Number mode")}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 has-[:checked]:border-gray-400 has-[:checked]:ring-1 has-[:checked]:ring-gray-300">
                    <input
                      type="radio"
                      name="wapp-assistant-mode"
                      value="exclusive"
                      checked={props.wappAssistantMode === "exclusive"}
                      onChange={() =>
                        props.onWappAssistantModeChange("exclusive")
                      }
                      className="mt-0.5 shrink-0"
                    />
                    <span className="min-w-0 space-y-1.5">
                      <span className="block text-sm font-medium text-gray-900">
                        {tr("Exclusivo", "Exclusive")}
                      </span>
                      <span className="block text-xs leading-snug text-gray-600">
                        {tr(
                          "Solo Eloria usa este número. Los mensajes manuales desde WhatsApp Business no están permitidos.",
                          "Only Eloria uses this number. Manual messages from WhatsApp Business are not allowed.",
                        )}
                      </span>
                      <span className="block text-xs leading-snug text-gray-600">
                        {tr(
                          "Si escribe alguien que no está registrado, Eloria le pide los datos para registrarlo. Se responde a todos los mensajes.",
                          "If someone unregistered writes, Eloria asks for their details to register them. Every message is replied to.",
                        )}
                      </span>
                    </span>
                  </label>
                  <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 has-[:checked]:border-gray-400 has-[:checked]:ring-1 has-[:checked]:ring-gray-300">
                    <input
                      type="radio"
                      name="wapp-assistant-mode"
                      value="shared"
                      checked={props.wappAssistantMode === "shared"}
                      onChange={() => props.onWappAssistantModeChange("shared")}
                      className="mt-0.5 shrink-0"
                    />
                    <span className="min-w-0 space-y-1.5">
                      <span className="block text-sm font-medium text-gray-900">
                        {tr("Compartido", "Shared")}
                      </span>
                      <span className="block text-xs leading-snug text-gray-600">
                        {tr(
                          "Los administradores pueden responder manualmente. Si un administrador responde, Eloria se pausa 1 hora para ese contacto.",
                          "Managers can reply manually. If a manager replies, Eloria pauses for 1 hour for that contact.",
                        )}
                      </span>
                      <span className="block text-xs leading-snug text-gray-600">
                        {tr(
                          "Solo se responde a los contactos registrados; el resto se ignora.",
                          "Only registered contacts are replied to; everyone else is ignored.",
                        )}
                      </span>
                      <span className="block text-xs leading-snug text-gray-600">
                        {tr(
                          `Los mensajes de Eloria se firman con 🤖 by ${props.assistantDisplayName}.`,
                          `Eloria's messages are signed with 🤖 by ${props.assistantDisplayName}.`,
                        )}
                      </span>
                    </span>
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  className="shrink-0"
                  onClick={props.onLaunchSignup}
                  disabled={!props.wappAppId || props.wappConnectLoading}
                  variant="primary"
                  size="sm"
                >
                  {props.wappConnectLoading
                    ? tr("Conectando...", "Connecting...")
                    : disconnected || props.wappTokenReconnectRecommended
                      ? tr("Reconectar WhatsApp", "Reconnect WhatsApp")
                      : tr("Conectar número", "Connect number")}
                </Button>
              </div>
            </div>
          ) : (
            <Link
              to="/settings?tab=assistant"
              className="mt-2 inline-block text-sm font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
            >
              {tr(
                "Cambiar número en Configuración",
                "Change number in Settings",
              )}
            </Link>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <WhatsappNumberConnectionIcon connected={false} />
            <p className="min-w-0 text-sm leading-snug text-gray-600">
              {tr(
                "No hay un número propio conectado.",
                "No personal number is connected.",
              )}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs leading-snug text-gray-600">
              {tr(
                "Actualmente estás usando el número de Eloria:",
                "You're currently using Eloria's number:",
              )}
            </p>
            <p className="min-w-0 text-base font-semibold tabular-nums tracking-tight text-gray-900">
              {formatEloriaWhatsAppDisplay(ELORIA_WHATSAPP_NUMBER)}
            </p>
          </div>
          {props.variant === "settings" ? (
            <div className="flex w-full flex-col items-start gap-2.5">
              <Button
                className="shrink-0"
                onClick={props.onLaunchSignup}
                disabled={!props.wappAppId || props.wappConnectLoading}
                variant="primary"
                size="sm"
              >
                {props.wappConnectLoading
                  ? tr("Conectando...", "Connecting...")
                  : tr("Conectar número", "Connect number")}
              </Button>
              <div className="w-full border-t border-gray-200/80 pt-3">
                <p className="text-xs leading-snug text-gray-500">
                  {tr(
                    "Puedes conectar un WhatsApp de la empresa, para responder a los residentes con un número propio.",
                    "You can connect a company WhatsApp to reply to residents with your own number.",
                  )}
                </p>
              </div>
            </div>
          ) : (
            <Link
              to="/settings?tab=assistant"
              className="inline-block text-sm font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
            >
              {tr(
                "Conectar tu número en Configuración",
                "Connect your number in Settings",
              )}
            </Link>
          )}
        </div>
      )}
      {props.variant === "settings" && props.wappConnectMessage ? (
        <p className="mt-3 text-xs leading-snug text-gray-600">
          {props.wappConnectMessage}
        </p>
      ) : null}
    </div>
  );
}
